// skills/farming/farmingData.js
// Farming crops, trees, and configuration

// Re-export FARM_CONFIG from constants for convenience
export { FARM_CONFIG } from '../../game/constants.js';

// Grass patch - infinite raking for seeds (active action)
// Only drops tier 1-2 seeds; higher tier seeds come from combat
export const GRASS_PATCH = {
    id: 'grass_patch',
    name: 'Grass Patch',
    level: 1,
    xp: 1,
    baseTime: 10000, // 10 seconds
    drops: [
        // Crop seeds (tier 1-2 only)
        { itemId: 'potato_seed', weight: 10, quantity: 1 },
        { itemId: 'onion_seed', weight: 10, quantity: 1 },
        // Herb seeds (tier 1-2 only)
        { itemId: 'greenleaf_seed', weight: 5, quantity: 1 },
        { itemId: 'duskleaf_seed', weight: 4, quantity: 1 }
        // Higher tier seeds come from combat
    ],
    color: '#7cba5f'
};

// Crops with real-time passive growth (minutes to hours)
export const CROPS = {
    POTATO: {
        id: 'potato',
        name: 'Potato',
        level: 1,
        xp: 150,
        growthTime: 15 * 60 * 1000, // 15 minutes
        seedId: 'potato_seed',
        seedCost: 1,
        drops: [
            { itemId: 'potato', quantity: 5 }
        ],
        color: '#d2691e',
        sprite: { sheet: 'assets/icons/farming/crops/potato.png', x: 0, y: 0, width: 64, height: 64 }
    },
    ONION: {
        id: 'onion',
        name: 'Onion',
        level: 5,
        xp: 250,
        growthTime: 25 * 60 * 1000, // 25 minutes
        seedId: 'onion_seed',
        seedCost: 1,
        drops: [
            { itemId: 'onion', quantity: 5 }
        ],
        color: '#dda0dd',
        sprite: { sheet: 'assets/icons/farming/crops/onion.png', x: 0, y: 0, width: 64, height: 64 }
    },
    CABBAGE: {
        id: 'cabbage',
        name: 'Cabbage',
        level: 7,
        xp: 350,
        growthTime: 35 * 60 * 1000, // 35 minutes
        seedId: 'cabbage_seed',
        seedCost: 1,
        drops: [
            { itemId: 'cabbage', quantity: 5 }
        ],
        color: '#90ee90',
        sprite: { sheet: 'assets/icons/farming/crops/cabbage.png', x: 0, y: 0, width: 64, height: 64 }
    },
    TOMATO: {
        id: 'tomato',
        name: 'Tomato',
        level: 12,
        xp: 500,
        growthTime: 50 * 60 * 1000, // 50 minutes
        seedId: 'tomato_seed',
        seedCost: 1,
        drops: [
            { itemId: 'tomato', quantity: 5 }
        ],
        color: '#ff6347',
        sprite: { sheet: 'assets/icons/farming/crops/tomato.png', x: 0, y: 0, width: 64, height: 64 }
    },
    CORN: {
        id: 'corn',
        name: 'Corn',
        level: 20,
        xp: 750,
        growthTime: 75 * 60 * 1000, // 1 hour 15 minutes
        seedId: 'corn_seed',
        seedCost: 1,
        drops: [
            { itemId: 'corn', quantity: 7 }
        ],
        color: '#f0e68c',
        sprite: { sheet: 'assets/icons/farming/crops/corn.png', x: 0, y: 0, width: 64, height: 64 }
    },
    STRAWBERRY: {
        id: 'strawberry',
        name: 'Strawberry',
        level: 31,
        xp: 1050,
        growthTime: 105 * 60 * 1000, // 1 hour 45 minutes
        seedId: 'strawberry_seed',
        seedCost: 1,
        drops: [
            { itemId: 'strawberry', quantity: 8 }
        ],
        color: '#ff1493',
        sprite: { sheet: 'assets/icons/farming/crops/strawberry.png', x: 0, y: 0, width: 64, height: 64 }
    },
    WATERMELON: {
        id: 'watermelon',
        name: 'Watermelon',
        level: 47,
        xp: 1500,
        growthTime: 150 * 60 * 1000, // 2 hours 30 minutes
        seedId: 'watermelon_seed',
        seedCost: 1,
        drops: [
            { itemId: 'watermelon', quantity: 10 }
        ],
        color: '#7cfc00',
        sprite: { sheet: 'assets/icons/farming/crops/watermelon.png', x: 0, y: 0, width: 64, height: 64 }
    },
    STARBLOOM: {
        id: 'starbloom',
        name: 'Starbloom',
        level: 62,
        xp: 2100,
        growthTime: 210 * 60 * 1000, // 3 hours 30 minutes
        seedId: 'starbloom_seed',
        seedCost: 1,
        drops: [
            { itemId: 'starbloom', quantity: 8 }
        ],
        color: '#ff00ff',
        sprite: { sheet: 'assets/icons/farming/crops/starbloom.png', x: 0, y: 0, width: 64, height: 64 }
    },
    KINGS_FRUIT: {
        id: 'kings_fruit',
        name: "King's Fruit",
        level: 85,
        xp: 3200,
        growthTime: 300 * 60 * 1000, // 5 hours
        seedId: 'kings_fruit_seed',
        seedCost: 1,
        drops: [
            { itemId: 'kings_fruit', quantity: 10 }
        ],
        color: '#4b0082',
        sprite: { sheet: 'assets/icons/farming/crops/kings_fruit.png', x: 0, y: 0, width: 64, height: 64 }
    }
};

// Herbs - produce grimy herbs for alchemy (passive growth)
export const HERBS = {
    GREENLEAF: {
        id: 'greenleaf',
        name: 'Green Leaf',
        level: 1,
        xp: 100,
        growthTime: 10 * 60 * 1000, // 10 minutes
        seedId: 'greenleaf_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_greenleaf', quantity: 3 }
        ],
        color: '#228B22',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_green_leaf.png', x: 0, y: 0, width: 64, height: 64 }
    },
    DUSKLEAF: {
        id: 'duskleaf',
        name: 'Dusk Leaf',
        level: 14,
        xp: 175,
        growthTime: 15 * 60 * 1000, // 15 minutes
        seedId: 'duskleaf_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_duskleaf', quantity: 4 }
        ],
        color: '#3CB371',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_dusk_leaf.png', x: 0, y: 0, width: 64, height: 64 }
    },
    IRONBLOOM: {
        id: 'ironbloom',
        name: 'Ironbloom',
        level: 19,
        xp: 225,
        growthTime: 20 * 60 * 1000, // 20 minutes
        seedId: 'ironbloom_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_ironbloom', quantity: 4 }
        ],
        color: '#32CD32',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_ironbloom.png', x: 0, y: 0, width: 64, height: 64 }
    },
    EMBERROOT: {
        id: 'emberroot',
        name: 'Emberroot',
        level: 26,
        xp: 300,
        growthTime: 25 * 60 * 1000, // 25 minutes
        seedId: 'emberroot_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_emberroot', quantity: 5 }
        ],
        color: '#7CFC00',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_emberroot.png', x: 0, y: 0, width: 64, height: 64 }
    },
    SANCTUM_LEAF: {
        id: 'sanctum_leaf',
        name: 'Sanctum Leaf',
        level: 32,
        xp: 400,
        growthTime: 30 * 60 * 1000, // 30 minutes
        seedId: 'sanctum_leaf_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_sanctum_leaf', quantity: 5 }
        ],
        color: '#00FF7F',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_sanctum_leaf.png', x: 0, y: 0, width: 64, height: 64 }
    },
    SUNPETAL: {
        id: 'sunpetal',
        name: 'Sunpetal',
        level: 44,
        xp: 550,
        growthTime: 45 * 60 * 1000, // 45 minutes
        seedId: 'sunpetal_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_sunpetal', quantity: 6 }
        ],
        color: '#2E8B57',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_sunpetal.png', x: 0, y: 0, width: 64, height: 64 }
    },
    DEEPVINE: {
        id: 'deepvine',
        name: 'Deepvine',
        level: 50,
        xp: 700,
        growthTime: 60 * 60 * 1000, // 1 hour
        seedId: 'deepvine_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_deepvine', quantity: 6 }
        ],
        color: '#8FBC8F',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_deepvine.png', x: 0, y: 0, width: 64, height: 64 }
    },
    STONEBARK: {
        id: 'stonebark',
        name: 'Stonebark',
        level: 56,
        xp: 900,
        growthTime: 75 * 60 * 1000, // 1 hour 15 minutes
        seedId: 'stonebark_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_stonebark', quantity: 7 }
        ],
        color: '#20B2AA',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_stonebark.png', x: 0, y: 0, width: 64, height: 64 }
    },
    BRIGHTMAW: {
        id: 'brightmaw',
        name: 'Brightmaw',
        level: 62,
        xp: 1025,
        growthTime: 82 * 60 * 1000, // 1 hour 22 minutes
        seedId: 'brightmaw_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_brightmaw', quantity: 7 }
        ],
        color: '#ff00ff',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_brightmaw.png', x: 0, y: 0, width: 64, height: 64 }
    },
    NIGHTVINE: {
        id: 'nightvine',
        name: 'Nightvine',
        level: 67,
        xp: 1150,
        growthTime: 90 * 60 * 1000, // 1.5 hours
        seedId: 'nightvine_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_nightvine', quantity: 7 }
        ],
        color: '#66CDAA',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_nightvine.png', x: 0, y: 0, width: 64, height: 64 }
    },
    GLOWSTEM: {
        id: 'glowstem',
        name: 'Glowstem',
        level: 73,
        xp: 1450,
        growthTime: 120 * 60 * 1000, // 2 hours
        seedId: 'glowstem_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_glowstem', quantity: 8 }
        ],
        color: '#5F9EA0',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_glowstem.png', x: 0, y: 0, width: 64, height: 64 }
    },
    ASHBLOOM: {
        id: 'ashbloom',
        name: 'Ashbloom',
        level: 79,
        xp: 1800,
        growthTime: 150 * 60 * 1000, // 2.5 hours
        seedId: 'ashbloom_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_ashbloom', quantity: 8 }
        ],
        color: '#4682B4',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_ashbloom.png', x: 0, y: 0, width: 64, height: 64 }
    },
    CROWNWORT: {
        id: 'crownwort',
        name: 'Crownwort',
        level: 85,
        xp: 2200,
        growthTime: 180 * 60 * 1000, // 3 hours
        seedId: 'crownwort_seed',
        seedCost: 1,
        drops: [
            { itemId: 'grimy_crownwort', quantity: 9 }
        ],
        color: '#6B8E23',
        sprite: { sheet: 'assets/icons/farming/herbs/grimy_crownwort.png', x: 0, y: 0, width: 64, height: 64 }
    }
};

// Trees - longer growth times, more XP (passive growth)
export const TREES = {
    IRONWOOD: {
        id: 'ironwood',
        name: 'Ironwood Tree',
        level: 15,
        xp: 1500,
        growthTime: 2 * 60 * 60 * 1000, // 2 hours
        seedId: 'ironwood_seed',
        seedCost: 1,
        drops: [
            { itemId: 'ironwood_logs', quantity: 10 }
        ],
        color: '#a0522d',
        sprite: { sheet: 'assets/icons/woodcutting/ironwood_tree.png', x: 0, y: 0, width: 64, height: 64 }
    },
    SOFTBARK: {
        id: 'softbark',
        name: 'Softbark Tree',
        level: 30,
        xp: 3500,
        growthTime: 4 * 60 * 60 * 1000, // 4 hours
        seedId: 'softbark_seed',
        seedCost: 1,
        drops: [
            { itemId: 'softbark_logs', quantity: 12 }
        ],
        color: '#9acd32',
        sprite: { sheet: 'assets/icons/woodcutting/softbark_tree.png', x: 0, y: 0, width: 64, height: 64 }
    },
    GOLDLEAF: {
        id: 'goldleaf',
        name: 'Goldleaf Tree',
        level: 45,
        xp: 6000,
        growthTime: 6 * 60 * 60 * 1000, // 6 hours
        seedId: 'goldleaf_seed',
        seedCost: 1,
        drops: [
            { itemId: 'goldleaf_logs', quantity: 15 }
        ],
        color: '#ff8c00',
        sprite: { sheet: 'assets/icons/woodcutting/goldleaf_tree.png', x: 0, y: 0, width: 64, height: 64 }
    },
    // Eldritch Sapling - grown from Ancient Seeds dropped by Deep Roots talent
    // Produces random logs and seeds - an amalgamation of all trees
    ELDRITCH: {
        id: 'eldritch',
        name: 'Eldritch Sapling',
        level: 50,
        xp: 20000,
        growthTime: 10 * 60 * 60 * 1000, // 10 hours
        seedId: 'ancient_seed',
        seedCost: 1,
        // Special random drop configuration
        randomDrops: {
            logs: { min: 15, max: 25, pool: ['ironwood_logs', 'softbark_logs', 'goldleaf_logs', 'redgrain_logs', 'gravewood_logs', 'arcanewood_logs'] },
            seeds: { min: 3, max: 5, pool: ['ironwood_seed', 'softbark_seed', 'goldleaf_seed', 'redgrain_seed', 'gravewood_seed', 'arcanewood_seed'] }
        },
        drops: [], // Empty - handled specially via randomDrops
        color: '#6b4423',
        // No sprite yet - uses color fallback
        description: 'A mysterious tree grown from an Ancient Seed. Produces a variety of logs and seeds when harvested.'
    },
    REDGRAIN: {
        id: 'redgrain',
        name: 'Redgrain Tree',
        level: 60,
        xp: 9500,
        growthTime: 8 * 60 * 60 * 1000, // 8 hours
        seedId: 'redgrain_seed',
        seedCost: 1,
        drops: [
            { itemId: 'redgrain_logs', quantity: 18 }
        ],
        color: '#d2691e',
        sprite: { sheet: 'assets/icons/woodcutting/redgrain_tree.png', x: 0, y: 0, width: 64, height: 64 }
    },
    GRAVEWOOD: {
        id: 'gravewood',
        name: 'Gravewood Tree',
        level: 75,
        xp: 12000,
        growthTime: 10 * 60 * 60 * 1000, // 10 hours
        seedId: 'gravewood_seed',
        seedCost: 1,
        drops: [
            { itemId: 'gravewood_logs', quantity: 22 }
        ],
        color: '#228b22',
        sprite: { sheet: 'assets/icons/woodcutting/gravewood_tree.png', x: 0, y: 0, width: 64, height: 64 }
    },
    ARCANEWOOD: {
        id: 'arcanewood',
        name: 'Arcanewood Tree',
        level: 90,
        xp: 18000,
        growthTime: 14 * 60 * 60 * 1000, // 14 hours
        seedId: 'arcanewood_seed',
        seedCost: 1,
        drops: [
            { itemId: 'arcanewood_logs', quantity: 30 }
        ],
        color: '#9370db',
        sprite: { sheet: 'assets/icons/woodcutting/arcanewood_tree.png', x: 0, y: 0, width: 64, height: 64 }
    }
};

// Tools provide harvest loot multipliers (bonus crops when harvesting)
export const TOOLS = {
    WOODEN: {
        id: 'wooden_rake',
        name: 'Wooden Rake',
        level: 1,
        lootMultiplier: 1.0,
        price: 0
    },
    PYRESTONE: {
        id: 'pyrestone_rake',
        name: 'Pyrestone Rake',
        level: 15,
        lootMultiplier: 1.1,
        price: 1000
    },
    BLACKIRON: {
        id: 'blackiron_rake',
        name: 'Blackiron Rake',
        level: 30,
        lootMultiplier: 1.2,
        price: 5000
    },
    PALE_SHARD: {
        id: 'pale_shard_rake',
        name: 'Pale Shard Rake',
        level: 45,
        lootMultiplier: 1.35,
        price: 25000
    },
    SKYIRON: {
        id: 'skyiron_rake',
        name: 'Skyiron Rake',
        level: 60,
        lootMultiplier: 1.5,
        price: 100000
    },
    VERDANT: {
        id: 'verdant_rake',
        name: 'Verdant Rake',
        level: 75,
        lootMultiplier: 1.75,
        price: 250000
    }
};

export default {
    GRASS_PATCH,
    CROPS,
    HERBS,
    TREES,
    TOOLS
};
